import React, { useState, useEffect } from 'react';
import { auth } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import useLimits from '../../hooks/useLimits';

/**
 * Checks if a URL is a valid YouTube URL
 * @param {string} url - URL to check
 * @returns {boolean} - Whether the URL is a valid YouTube URL
 */
export const isYouTubeUrl = (url) => {
  const pattern = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
  return pattern.test(url);
};

/**
 * Extracts the YouTube video ID from a URL
 * @param {string} url - YouTube URL
 * @returns {string|null} - The video ID, or null if it couldn't be extracted
 */
export const extractYouTubeID = (url) => {
  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[7].length === 11) ? match[7] : null;
};

/**
 * YouTubeURL component for adding YouTube videos
 */
const YouTubeURL = ({ isOpen, onClose, onSubmit, onStartGlobalLoading, onStopGlobalLoading }) => {
  const [urlInput, setUrlInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const navigate = useNavigate();
  const { uploads } = useLimits();
  const isLimitReached = uploads !== undefined && uploads <= 0;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setUrlInput('');
      setApiError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (uploads !== undefined && uploads <= 0) {
      setApiError('You have reached your daily upload limit.');
      return;
    }
    
    if (!urlInput.trim()) return;
    
    const url = urlInput.trim();
    if (!isYouTubeUrl(url)) {
      setApiError('Please enter a valid YouTube URL');
      return;
    }
    
    let finalId = null;

    try {
      if (!auth.currentUser) {
        setApiError("Authentication failed. Please sign in again.");
        return;
      }

      // Close the modal, then start card overlay
      onClose();
      setTimeout(() => onStartGlobalLoading?.('Fetching YouTube content...'), 30);

      // Force refresh token to ensure it's valid
      const idToken = await auth.currentUser.getIdToken(true);
      
      try {
        setIsLoading(true);
        setApiError(null);

        const videoId = extractYouTubeID(url);
        if (!videoId) {
          throw new Error('Could not extract YouTube video ID');
        }
        
        // Call YouTube content API
        const YT_CONTENT_API = process.env.REACT_APP_YOUTUBE_CONTENT_API;
        const apiUrl = `${YT_CONTENT_API}?id=${videoId}&uid=${idToken}`;
        
        const response = await fetch(apiUrl, { 
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          }
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`API error (${response.status}): ${errorText}`);
        }
        
        const data = await response.json();
        const contentId = data.result?.document_id || videoId;
        
        const result = await onSubmit({ 
          type: 'youtube', 
          content: url, 
          videoId: videoId,
          data: data,
          contentId
        });

        finalId = contentId || result?.contentId || null;
      } catch (error) {
        const videoId = extractYouTubeID(url);
        if (videoId) {
          const result = await onSubmit({ 
            type: 'youtube', 
            content: url,
            videoId: videoId,
            fallbackMessage: "Couldn't fetch transcript. Displaying video only."
          });
          finalId = result?.contentId || null;
        } else {
          setApiError('Could not process YouTube URL. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    } catch (authError) {
      setApiError("Authentication failed. Please sign in again.");
    }

    onStopGlobalLoading?.({ success: !!finalId, message: finalId ? 'Opening chat...' : 'Processing failed' });
    if (finalId) {
      setTimeout(() => {
        navigate(`/c/${finalId}`);
      }, 50);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black bg-opacity-70" 
        onClick={onClose}
      ></div>

      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <form onSubmit={handleSubmit} className="p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Add YouTube Video</h2>

          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Enter a YouTube URL
            </label>
            <input
              type="text"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://youtu.be/dQw4w9WgXcQ"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
              disabled={isLimitReached}
            />
            {isLimitReached && (
              <div className="text-red-600 text-sm" role="alert" aria-live="polite">
                You have reached your daily upload limit.
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            
            <button
              type="submit"
              disabled={isLoading || !urlInput.trim() || isLimitReached}
              className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors disabled:opacity-70 disabled:hover:bg-blue-600"
            >
              {isLoading ? 'Processing...' : 'Add Video'}
            </button>
          </div>

          {isLoading && (
            <div className="text-blue-600 mt-2">Loading video and transcript data...</div>
          )}
          {apiError && !isLoading && (
            <div className="text-red-600 mt-2">{apiError}</div>
          )}
        </form>
      </div>
    </div>
  );
};

export default YouTubeURL;
